import { Divider, Typography } from '@material-ui/core'
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined'

import { formatPromotionMinorAmount } from '../../helpers/promotion'
import { getTranslate } from '../../localization'
import { AppliedPromotion, PromotionApplication } from '../../types/promotion'
import useStyles from './styles'

type PromotionSummaryProps = {
  application?: PromotionApplication
}

function stateMessage(application: PromotionApplication): {
  key: string
  warning?: boolean
} {
  switch (application.state) {
    case 'NoEligiblePromotion':
      return { key: 'No eligible promotion was found for this booking.' }
    case 'EvaluationUnavailable':
      return {
        key: 'Promotion evaluation is temporarily unavailable. The shown price does not include promotion savings.',
        warning: true
      }
    case 'ReleasePending':
      return {
        key: 'Promotion release is pending. The booking may be recalculated.',
        warning: true
      }
    case 'Released':
      return { key: 'The promotion is no longer applied to this booking.' }
    case 'Confirmed':
      return { key: 'Promotion confirmed for this booking.' }
    case 'Reserved':
      return { key: 'Promotion reserved for this booking.' }
    default:
      // An unrecognised state must not ASSERT that a promotion applied. Saying "applied"
      // beside an empty list tells a booker a discount is on their booking when none is —
      // the one reading of this card that is worse than saying nothing. A state we do not
      // know is a state we report as unknown.
      return application.applied.length > 0
        ? { key: 'Promotion applied to this booking.' }
        : {
            key: 'Promotion status for this booking is unknown.',
            warning: true
          }
  }
}

/**
 * `1,250 IRR` — the amount and its unit, which appear together everywhere in this card and
 * were written out three times before.
 */
function amountWithCurrency(minorAmount: string, currency: string): string {
  return `${formatPromotionMinorAmount(minorAmount, currency)} ${getTranslate(
    currency
  )}`
}

/**
 * One applied promotion and the room stays it landed on.
 *
 * Extracted so PromotionSummary stays readable, not to make it reusable — it has exactly
 * one caller and is not exported. Kept structurally identical to the admin-panel twin: the
 * two apps show the same booking to the booker and to the agent acting for them, and the
 * copies were split the same way so a later change to one is visibly a change to both.
 */
const PromotionRow = ({ promotion }: { promotion: AppliedPromotion }) => {
  const classes = useStyles()

  return (
    <div className={classes.promotion}>
      <div className={classes.row}>
        <div className={classes.details}>
          <Typography variant="h5" className={classes.title}>
            {promotion.name}
          </Typography>
          <Typography variant="h6" className={classes.message}>
            {getTranslate(
              promotion.distribution === 'CodeEntry'
                ? 'Promotion code applied'
                : 'Automatic promotion'
            )}
          </Typography>
        </div>
        <Typography variant="h6" className={classes.amount}>
          {amountWithCurrency(promotion.discountMinor, promotion.currency)}
        </Typography>
      </div>
      {promotion.allocations.map((allocation) => (
        <div className={classes.row} key={allocation.roomStayReference}>
          <Typography variant="h6" className={classes.allocation}>
            {getTranslate('Room stay {reference}', {
              reference: allocation.roomStayReference
            })}
          </Typography>
          <Typography variant="h6" className={classes.allocation}>
            {amountWithCurrency(allocation.discountMinor, allocation.currency)}
          </Typography>
        </div>
      ))}
    </div>
  )
}

/**
 * The card's bottom line. Rendered only when something actually applied — a "savings" row
 * reading zero next to no promotions invites the reader to hunt for a discount that was
 * never there.
 */
const SavingsTotal = ({
  totalDiscountMinor,
  currency
}: {
  totalDiscountMinor: string
  currency: string
}) => {
  const classes = useStyles()

  return (
    <>
      <Divider className={classes.divider} />
      <div className={classes.row}>
        <Typography variant="h5" className={classes.title}>
          {getTranslate('Promotion savings')}
        </Typography>
        <Typography variant="h5" className={classes.amount}>
          {amountWithCurrency(totalDiscountMinor, currency)}
        </Typography>
      </div>
    </>
  )
}

const PromotionSummary = ({ application }: PromotionSummaryProps) => {
  const classes = useStyles()

  if (
    !application ||
    (application.applied.length === 0 && application.state === undefined)
  ) {
    return null
  }

  const status = stateMessage(application)
  const hasAppliedPromotions = application.applied.length > 0

  return (
    <section className={classes.card} aria-label={getTranslate('Promotions')}>
      <div className={classes.header}>
        <LocalOfferOutlinedIcon className={classes.icon} />
        <Typography variant="h4" className={classes.title}>
          {getTranslate('Promotions')}
        </Typography>
      </div>
      <Typography
        variant="h6"
        className={[
          classes.message,
          status.warning ? classes.warning : '',
          hasAppliedPromotions ? classes.success : ''
        ].join(' ')}>
        {getTranslate(status.key)}
      </Typography>

      {application.applied.map((promotion) => (
        <PromotionRow key={promotion.promotionId} promotion={promotion} />
      ))}

      {hasAppliedPromotions && (
        <SavingsTotal
          totalDiscountMinor={application.totalDiscountMinor}
          currency={application.currency}
        />
      )}
    </section>
  )
}

export default PromotionSummary
